import * as React from 'react';
import * as API from '../Utils/API.js';

type Props =
  { componentName: string
  , children: React.ReactNode
  }

type State =
  { hasError: boolean
  }

// A single boundary reports at most once (React unmounts the failed subtree and renders the
// fallback, so it can't re-catch), but a page can mount many boundaries, and a component that
// remounts in a loop could catch repeatedly. Cap reports per page load so a pathological render
// loop can't turn every client into an Airbrake flood — the first few reports carry the same
// diagnostic value as the thousandth.
const MAX_REPORTS_PER_PAGE_LOAD = 3;
let reportsThisPageLoad = 0;

// Without a boundary, any error thrown while rendering (or inside an effect, which React
// re-throws during the commit phase) unmounts the entire React root and leaves the user
// staring at a blank page with no indication that anything went wrong. This contains the
// damage to the one component that failed.
//
// NOTE: this is inert during server rendering — React does not invoke error boundaries in
// renderToString — so it protects the client only. That is where the unmount happens.
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Error rendering <${this.props.componentName} />`, error, errorInfo);

    if (reportsThisPageLoad >= MAX_REPORTS_PER_PAGE_LOAD) return;
    reportsThisPageLoad += 1;

    // Report to the server so Airbrake sees this. A contained inline alert is much easier for a
    // user to ignore than the blank page it replaces, so without this the boundary would make
    // render bugs quieter rather than more visible.
    API.post('api_client_errors_report_path', {
      component: this.props.componentName,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href
    }).catch(() => {
      // Reporting is best-effort — never let it throw out of the boundary that's containing a crash.
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="alert alert-danger" role="alert">
          <div className="fw-bold">Something went wrong.</div>
          <div className="fs-sm">
            We couldn't display this section.{' '}
            <button
              type="button"
              className="btn btn-link alert-link p-0 align-baseline"
              onClick={() => window.location.reload()}
            >
              Reload the page
            </button>{' '}
            to try again.
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wraps a component so that it fails to a message rather than to a blank page. An ErrorBoundary
// renders its children with no wrapper element, so this adds no DOM and cannot introduce a
// hydration mismatch.
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  componentName: string
): React.FC<P> {
  const Wrapped: React.FC<P> = (props: P) => (
    <ErrorBoundary componentName={componentName}>
      <Component {...props} />
    </ErrorBoundary>
  );

  Wrapped.displayName = `withErrorBoundary(${componentName})`;

  return Wrapped;
}
